import React, { useState } from "react";
import { useQuery } from "react-query";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  TextField,
  Typography,
  Paper,
  Divider,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getUploadedDocuments, deleteUploadedDocument } from "../registrationServices";
import { UseFormReturn } from "react-hook-form";
import { DocumentUploadType } from "../type/RegistrationModel";

interface FileUploadModalProps {
  formControls: UseFormReturn<any>;
  onClose?: () => void;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  formControls,
  onClose,
}) => {
  const { setValue, watch } = formControls;
  const initialDocuments = watch("documents") || [];
  const emrNo = watch("patientId"); // Getting emrno from form
  const [files, setFiles] = useState<DocumentUploadType[]>(initialDocuments);

  const { isLoading: existingLoading } = useQuery(
    ["uploadedDocuments", emrNo],
    () => getUploadedDocuments(emrNo!),
    {
      enabled: Boolean(emrNo && emrNo !== "0"),
      onSuccess: (res) => {
        if (
          res.responseIndicator === "success" &&
          res.response &&
          files.length === 0
        ) {
          setFiles(res.response);
        }
      },
    }
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map((file) => ({
        file,
        documentType: "",
      }));
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleUpdateFile = (index: number, updates: Partial<DocumentUploadType>) => {
    const updatedFiles = [...files];
    updatedFiles[index] = { ...updatedFiles[index], ...updates };
    setFiles(updatedFiles);
  };

  const removeFile = async (index: number) => {
    const fileToRemove = files[index];
    
    if (window.confirm("Are you sure you want to delete this document?")) {
      if (fileToRemove.url && fileToRemove.id) {
        try {
          const res = await deleteUploadedDocument(fileToRemove.id);
          if (res.responseIndicator === "success") {
            setFiles((prev) => prev.filter((_, i) => i !== index));
          } else {
            alert(res.responseMessage || "Failed to delete document");
          }
        } catch (error) {
          console.error("Error deleting document:", error);
          alert("An error occurred while deleting the document");
        }
      } else {
        // For new local files
        setFiles((prev) => prev.filter((_, i) => i !== index));
      }
    }
  };

  const handleSave = () => {
    setValue("documents", files);
    if (onClose) onClose();
  };

  return (
    <Box sx={{ p: 3, width: 600, maxWidth: "100%" }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" color="primary">Manage Documents</Typography>
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUploadIcon />}
        >
          Add Files
          <input type="file" multiple hidden onChange={handleFileChange} />
        </Button>
      </Box>

      {(files.length > 0 || existingLoading) ? (
        <Paper variant="outlined" sx={{ maxHeight: 400, overflow: "auto", mb: 3 }}>
          {existingLoading && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2">Loading existing documents...</Typography>
            </Box>
          )}
          <List>
            {files.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{ flexDirection: "column", alignItems: "stretch", py: 2 }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: item.url ? "text.secondary" : "text.primary" }}>
                      {item.url ? item.fileName : item.file?.name} 
                      {!item.url && item.file && ` (${(item.file.size / 1024).toFixed(1)} KB)`}
                    </Typography>
                    <Box>
                        {item.url && (
                            <Tooltip title="View Document">
                                <IconButton size="small" component="a" href={item.url} target="_blank" rel="noopener noreferrer" color="primary">
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <IconButton size="small" onClick={() => removeFile(index)} color="error">
                        <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Document Name"
                      value={item.documentType || ""}
                      onChange={(e) =>
                        handleUpdateFile(index, { documentType: e.target.value })
                      }
                      disabled={Boolean(item.url)}
                    />
                  </Box>
                </ListItem>
                {index < files.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      ) : (
        <Box sx={{ py: 8, textAlign: "center", border: "2px dashed #ccc", borderRadius: 2, mb: 3 }}>
            <Typography color="textSecondary">No files selected</Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSave}
        >
          Apply Changes
        </Button>
      </Box>
    </Box>
  );
};

export default FileUploadModal;
